import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Link, useNavigate, useParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faUser,
  faPhone,
  faEnvelope,
  faHome,
  faCity,
  faMailBulk,
  faCalendarDays,
  faClock,
  faCircleExclamation,
  faCircleCheck,
  faStore,
} from "@fortawesome/free-solid-svg-icons";

import { useHerKart } from "./hooks/useHerKart";

const SLOTS = [
  "9:00 AM - 11:00 AM",
  "11:00 AM - 1:00 PM",
  "2:00 PM - 4:00 PM",
  "4:00 PM - 6:00 PM",
  "6:00 PM - 8:00 PM",
];

const HerKartServiceCheckout = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const authState = useSelector((state) => state.authReducer);
  const { fetchLiveServices, bookService } = useHerKart();

  const [liveServices, setLiveServices] = useState([]);
  const [loadingService, setLoadingService] = useState(true);
  const [placing, setPlacing] = useState(false);
  const [message, setMessage] = useState(null);

  const [form, setForm] = useState({
    name: authState?.user?.name || "",
    mobile: authState?.user?.mobile || "",
    email: authState?.user?.email || "",
    house: "",
    street: "",
    landmark: authState?.user?.area || "",
    city: authState?.user?.city || "",
    state: authState?.user?.state || "",
    pincode: authState?.user?.pinCode || "",
    notes: "",
  });
  const [preferredDate, setPreferredDate] = useState("");
  const [preferredSlot, setPreferredSlot] = useState(SLOTS[0]);
  const [errors, setErrors] = useState({});

  const loadService = async () => {
    setLoadingService(true);
    try {
      const response = await fetchLiveServices();
      if (response?.success) {
        setLiveServices(response.services || []);
      }
    } catch (e) {
      console.error("Service fetch error:", e);
    } finally {
      setLoadingService(false);
    }
  };

  useEffect(() => {
    loadService();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const service = useMemo(
    () => liveServices.find((item) => String(item._id) === String(id)),
    [liveServices, id]
  );

  const minDate = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() + 1); // earliest bookable = tomorrow
    return d.toISOString().split("T")[0];
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validate = () => {
    const nextErrors = {};
    if (!form.name.trim()) nextErrors.name = "Name is required";
    if (!/^[6-9]\d{9}$/.test(form.mobile)) nextErrors.mobile = "Enter a valid mobile number";
    if (!form.house.trim()) nextErrors.house = "House / Flat is required";
    if (!form.city.trim()) nextErrors.city = "City is required";
    if (!form.state.trim()) nextErrors.state = "State is required";
    if (!/^\d{6}$/.test(form.pincode)) nextErrors.pincode = "Enter a valid PIN code";
    if (!preferredDate) nextErrors.preferredDate = "Pick a preferred date";

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleBook = async () => {
    setMessage(null);
    if (!service) return;

    if (!validate()) {
      setMessage({ type: "error", text: "Please fix the highlighted fields before continuing." });
      return;
    }

    setPlacing(true);
    try {
      const payload = {
        serviceId: service._id,
        customer: {
          name: form.name,
          mobile: form.mobile,
          email: form.email,
          house: form.house,
          street: form.street,
          landmark: form.landmark,
          city: form.city,
          state: form.state,
          pincode: form.pincode,
          notes: form.notes,
        },
        preferredDate,
        preferredSlot,
      };

      const response = await bookService(payload);

      if (response?.success) {
        navigate("/herkart/service-bookings/success", { state: { booking: response.booking } });
      } else {
        setMessage({ type: "error", text: "Failed to place booking. Please try again." });
      }
    } catch (e) {
      console.error("Book service error:", e);
      setMessage({ type: "error", text: "Failed to place booking. Please try again." });
    } finally {
      setPlacing(false);
    }
  };

  if (loadingService) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#050816] text-white">
        <p className="text-slate-300">Loading service...</p>
      </div>
    );
  }

  if (!service) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-[#050816] text-white">
        <p className="text-slate-300">This service is no longer available.</p>
        <Link to="/herkart/services" className="rounded-xl bg-pink-600 px-5 py-2 font-semibold hover:bg-pink-500">
          Back to Services
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050816] text-white">
      <div className="sticky top-0 z-40 border-b border-white/10 bg-[#050816]/95 backdrop-blur">
        <div className="mx-auto flex max-w-3xl items-center gap-3 px-4 py-3">
          <Link
            to={`/herkart/services/${service._id}`}
            className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/10 hover:bg-white/20"
          >
            <FontAwesomeIcon icon={faArrowLeft} />
          </Link>
          <div>
            <h1 className="text-lg font-bold">Book Service</h1>
            <p className="text-xs text-slate-400">Cash on Service</p>
          </div>
        </div>
      </div>

      {message && (
        <div className="mx-auto max-w-3xl px-4 pt-4">
          <div
            className={`flex items-center gap-3 rounded-2xl border p-4 text-sm ${
              message.type === "success"
                ? "border-emerald-500/20 bg-emerald-500/10 text-emerald-200"
                : "border-red-500/20 bg-red-500/10 text-red-200"
            }`}
          >
            <FontAwesomeIcon icon={message.type === "success" ? faCircleCheck : faCircleExclamation} />
            {message.text}
          </div>
        </div>
      )}

      <div className="mx-auto max-w-3xl px-4 py-6">
        <div className="rounded-3xl border border-white/10 bg-white/5 p-5">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-bold">{service.name}</h2>
              <p className="mt-1 flex items-center gap-2 text-sm text-slate-400">
                <FontAwesomeIcon icon={faStore} className="text-pink-400" />
                {service.entrepreneur || service.store} — single provider only
              </p>
            </div>
            <div className="text-2xl font-extrabold text-pink-400">₹{service.price}</div>
          </div>
        </div>

        <div className="mt-6 rounded-3xl border border-white/10 bg-white/5 p-5">
          <h3 className="mb-4 font-semibold text-cyan-200">Your Details</h3>

          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <label className="mb-1 flex items-center gap-2 text-sm text-gray-300">
                <FontAwesomeIcon icon={faUser} /> Name
              </label>
              <input
                name="name"
                value={form.name}
                onChange={handleChange}
                className="w-full rounded-xl border border-white/10 bg-[#0b1120] px-4 py-3 outline-none focus:border-pink-500"
              />
              {errors.name && <p className="mt-1 text-xs text-red-400">{errors.name}</p>}
            </div>

            <div>
              <label className="mb-1 flex items-center gap-2 text-sm text-gray-300">
                <FontAwesomeIcon icon={faPhone} /> Mobile
              </label>
              <input
                name="mobile"
                value={form.mobile}
                onChange={handleChange}
                className="w-full rounded-xl border border-white/10 bg-[#0b1120] px-4 py-3 outline-none focus:border-pink-500"
              />
              {errors.mobile && <p className="mt-1 text-xs text-red-400">{errors.mobile}</p>}
            </div>

            <div className="md:col-span-2">
              <label className="mb-1 flex items-center gap-2 text-sm text-gray-300">
                <FontAwesomeIcon icon={faEnvelope} /> Email (optional)
              </label>
              <input
                name="email"
                value={form.email}
                onChange={handleChange}
                className="w-full rounded-xl border border-white/10 bg-[#0b1120] px-4 py-3 outline-none focus:border-pink-500"
              />
            </div>

            <div className="md:col-span-2">
              <label className="mb-1 flex items-center gap-2 text-sm text-gray-300">
                <FontAwesomeIcon icon={faHome} /> House / Flat
              </label>
              <input
                name="house"
                value={form.house}
                onChange={handleChange}
                className="w-full rounded-xl border border-white/10 bg-[#0b1120] px-4 py-3 outline-none focus:border-pink-500"
              />
              {errors.house && <p className="mt-1 text-xs text-red-400">{errors.house}</p>}
            </div>

            <div className="md:col-span-2">
              <label className="mb-1 block text-sm text-gray-300">Street / Area / Landmark</label>
              <input
                name="street"
                value={form.street}
                onChange={handleChange}
                placeholder="Street / Area"
                className="mb-2 w-full rounded-xl border border-white/10 bg-[#0b1120] px-4 py-3 outline-none focus:border-pink-500"
              />
              <input
                name="landmark"
                value={form.landmark}
                onChange={handleChange}
                placeholder="Landmark (optional)"
                className="w-full rounded-xl border border-white/10 bg-[#0b1120] px-4 py-3 outline-none focus:border-pink-500"
              />
            </div>

            <div>
              <label className="mb-1 flex items-center gap-2 text-sm text-gray-300">
                <FontAwesomeIcon icon={faCity} /> City
              </label>
              <input
                name="city"
                value={form.city}
                onChange={handleChange}
                className="w-full rounded-xl border border-white/10 bg-[#0b1120] px-4 py-3 outline-none focus:border-pink-500"
              />
              {errors.city && <p className="mt-1 text-xs text-red-400">{errors.city}</p>}
            </div>

            <div>
              <label className="mb-1 block text-sm text-gray-300">State</label>
              <input
                name="state"
                value={form.state}
                onChange={handleChange}
                className="w-full rounded-xl border border-white/10 bg-[#0b1120] px-4 py-3 outline-none focus:border-pink-500"
              />
              {errors.state && <p className="mt-1 text-xs text-red-400">{errors.state}</p>}
            </div>

            <div>
              <label className="mb-1 flex items-center gap-2 text-sm text-gray-300">
                <FontAwesomeIcon icon={faMailBulk} /> PIN Code
              </label>
              <input
                name="pincode"
                value={form.pincode}
                onChange={handleChange}
                className="w-full rounded-xl border border-white/10 bg-[#0b1120] px-4 py-3 outline-none focus:border-pink-500"
              />
              {errors.pincode && <p className="mt-1 text-xs text-red-400">{errors.pincode}</p>}
            </div>
          </div>
        </div>

        <div className="mt-6 rounded-3xl border border-white/10 bg-white/5 p-5">
          <h3 className="mb-4 font-semibold text-cyan-200">Preferred Date & Slot</h3>

          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <label className="mb-1 flex items-center gap-2 text-sm text-gray-300">
                <FontAwesomeIcon icon={faCalendarDays} /> Date
              </label>
              <input
                type="date"
                min={minDate}
                value={preferredDate}
                onChange={(e) => {
                  setPreferredDate(e.target.value);
                  setErrors((prev) => ({ ...prev, preferredDate: "" }));
                }}
                className="w-full rounded-xl border border-white/10 bg-[#0b1120] px-4 py-3 outline-none focus:border-pink-500"
              />
              {errors.preferredDate && <p className="mt-1 text-xs text-red-400">{errors.preferredDate}</p>}
            </div>

            <div>
              <label className="mb-1 flex items-center gap-2 text-sm text-gray-300">
                <FontAwesomeIcon icon={faClock} /> Slot
              </label>
              <select
                value={preferredSlot}
                onChange={(e) => setPreferredSlot(e.target.value)}
                className="w-full rounded-xl border border-white/10 bg-[#0b1120] px-4 py-3 outline-none focus:border-pink-500"
              >
                {SLOTS.map((slot) => (
                  <option key={slot} value={slot}>
                    {slot}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="mt-4">
            <label className="mb-1 block text-sm text-gray-300">Notes for the provider (optional)</label>
            <textarea
              name="notes"
              value={form.notes}
              onChange={handleChange}
              rows={3}
              placeholder="Anything the provider should know before arriving..."
              className="w-full rounded-xl border border-white/10 bg-[#0b1120] px-4 py-3 outline-none focus:border-pink-500"
            />
          </div>
        </div>

        <button
          onClick={handleBook}
          disabled={placing}
          className="mt-6 w-full rounded-2xl bg-gradient-to-r from-pink-600 via-fuchsia-600 to-purple-600 px-5 py-4 text-lg font-semibold text-white shadow-lg transition hover:scale-[1.01] disabled:opacity-60"
        >
          {placing ? "Placing Booking..." : `Confirm Booking — ₹${service.price}`}
        </button>
      </div>
    </div>
  );
};

export default HerKartServiceCheckout;
